/**
 * Google Play product catalog for the Capacitor shell.
 * Single source of truth for client checkout and Convex grants.
 *
 * Product IDs are the agreed Play Console proposal. Do not rename them here
 * if TOP_UP_TIER_CREDITS or plan slugs change — fix the drift in the open.
 *
 * Play keeps about 15% of each purchase. Intermediate Play list TT$ =
 * round(web TT$ / 0.85). Play Console is entered in **USD** using Studio's
 * official generation FX (`USD_TO_TTD = 10`): playListUsd = playListTtd / 10.
 * Credit grants, course unlocks, and plan face credits stay the web amounts.
 * `playListTtdFromWebCents` / `playListUsdFromPlayListTtd` are for Console
 * setup only. The app does not display these faces; Google shows the
 * localized price on the purchase sheet.
 *
 * Examples (web TT$ → Play list TT$ → Play USD): credits_starter_100
 * 50 → 59 → $5.90; credits_pro_1000 500 → 588 → $58.80;
 * credits_studio_2000 1000 → 1176 → $117.60; credits_scale_4000
 * 2000 → 2353 → $235.30; plan_core monthly 150 → 176 → $17.60, annual
 * 1746 → 2054 → $205.40; plan_plus monthly 339.50 → 399 → $39.90, yearly
 * 3864 → 4546 → $454.60; plan_pro monthly 920 → 1082 → $108.20, yearly
 * 10560 → 12424 → $1242.40. Academy: ad-side-hustle / video-ad-mastery
 * online 1500 → 1765 → $176.50; short-films-studio 2500 → 2941 → $294.10;
 * cinematic-film-mastery 4000 → 4706 → $470.60. In-person Video Ad Mastery
 * TT$2000 stays Wam-only (no Play SKU). Product IDs are locked for Sophie.
 */
import {
  DEFAULT_CREDIT_PRICE_CENTS,
  TOP_UP_TIER_CREDITS,
} from "../../src/studio/lib/money";
import { USD_TO_TTD } from "./generationPricing";
import {
  quoteStudioPlan,
  STUDIO_PLAN_CATALOG,
  type StudioPlanCatalogEntry,
  type StudioPlanInterval,
} from "./studioPlans";

export const PLAY_PACKAGE_NAME = "com.yatishara.studio";

/**
 * Share of the Play list price Yatishara keeps after the store fee.
 * Intermediate Play list TT$ = round(web TT$ / PLAY_FEE_KEEP_RATE).
 * Play Console USD = that TT$ / USD_TO_TTD (Studio generation FX = 10).
 */
export const PLAY_FEE_KEEP_RATE = 0.85;

/** Re-export so Console tables stay on the same FX as generation pricing. */
export { USD_TO_TTD as PLAY_USD_TO_TTD };

/**
 * Intermediate whole TT dollars after covering Play's ~15% fee.
 * Rounds the dollar amount, not the cent amount: Plus monthly web is
 * TT$339.50, and round(339.50 / 0.85) is 399 (round(33950 / 0.85) is not).
 * Convert to USD with `playListUsdFromPlayListTtd` before Console entry.
 */
export function playListTtdFromWebCents(webCents: number): number {
  const webTtd = Math.round(Number(webCents) || 0) / 100;
  if (webTtd <= 0) return 0;
  return Math.round(webTtd / PLAY_FEE_KEEP_RATE);
}

/**
 * USD dollars to enter in Play Console (default price / US template).
 * playListTtd / USD_TO_TTD. Keeps two decimal places (e.g. 59 → 5.90).
 */
export function playListUsdFromPlayListTtd(playListTtd: number): number {
  const ttd = Number(playListTtd) || 0;
  if (ttd <= 0) return 0;
  return Math.round((ttd / USD_TO_TTD) * 100) / 100;
}

/** web TT cents → Play Console USD via fee markup then Studio FX. */
export function playListUsdFromWebCents(webCents: number): number {
  return playListUsdFromPlayListTtd(playListTtdFromWebCents(webCents));
}

/** Production Convex hosts. Preview must not grant Play purchases. */
export const LIVE_PLAY_DEPLOYMENT_HOSTS = [
  "convex-studio.yatishara.com",
  "convex-studio-api.yatishara.com",
] as const;

/**
 * Play Console base-plan IDs.
 * - All plans: `monthly` for month.
 * - Core year: `annual` (Console Active). Core also has an INACTIVE broken
 *   `yearly` P1M shell — never purchase that; grant restore still accepts it.
 * - Plus / Pro year: `yearly`.
 */
export const PLAY_BASE_PLANS = [
  { basePlanId: "monthly", interval: "month" },
  { basePlanId: "yearly", interval: "year" },
  { basePlanId: "annual", interval: "year" },
] as const satisfies ReadonlyArray<{
  basePlanId: "monthly" | "yearly" | "annual";
  interval: StudioPlanInterval;
}>;

/** Year basePlanId to offer at purchase time (matches Active Console SKUs). */
export function playYearBasePlanIdForPlan(
  planSlug: string,
): "annual" | "yearly" {
  return planSlug === "core" ? "annual" : "yearly";
}

/**
 * Consumable credit packs. Credit counts must stay equal to TOP_UP_TIER_CREDITS.
 * Labels: Starter, Pro, Studio, Scale.
 */
export const PLAY_CREDIT_PRODUCTS = [
  { productId: "credits_starter_100", credits: 100 },
  { productId: "credits_pro_1000", credits: 1000 },
  { productId: "credits_studio_2000", credits: 2000 },
  { productId: "credits_scale_4000", credits: 4000 },
] as const;

/**
 * One-time Academy products. Product IDs are locked.
 * webListCents is the web face this SKU covers (TT cents). Play list TT$
 * is playListTtdFromWebCents(webListCents). The course unlock is the slug;
 * the Play price is not a second grant.
 *
 * - academy_ad_side_hustle → ad-side-hustle. TT$500 sale ended 2026-09-01;
 *   Console uses the TT$1500 list (Play TT$1765 → USD $176.50).
 * - academy_video_ad_mastery → video-ad-mastery (VIDEO_AD_MASTERY_SLUG) at
 *   the **online** seat, web TT$1500 (Play TT$1765 → USD $176.50). In-person TT$2000 is
 *   Wam/web only. There is no Play SKU for that seat. Catalog listPriceCredits
 *   stays the in-person face for the website.
 * - academy_short_films_studio → short-films-studio, web TT$2500 (Play TT$2941 → USD $294.10).
 *   Coming soon; the Play product may stay inactive until the course unlocks.
 * - academy_cinematic_film_mastery → cinematic-film-mastery, web TT$4000
 *   (Play TT$4706 → USD $470.60). Coming soon; the Play product may stay inactive.
 */
export const PLAY_ACADEMY_PRODUCTS = [
  {
    productId: "academy_ad_side_hustle",
    courseSlug: "ad-side-hustle",
    webListCents: 150_000,
  },
  {
    productId: "academy_video_ad_mastery",
    courseSlug: "video-ad-mastery",
    webListCents: 150_000,
    seat: "online",
  },
  {
    productId: "academy_short_films_studio",
    courseSlug: "short-films-studio",
    webListCents: 250_000,
  },
  {
    productId: "academy_cinematic_film_mastery",
    courseSlug: "cinematic-film-mastery",
    webListCents: 400_000,
  },
] as const;

export const PLAY_SUBSCRIPTION_PRODUCTS = STUDIO_PLAN_CATALOG.map((plan) => {
  const yearBasePlanId = playYearBasePlanIdForPlan(plan.slug);
  const yearBase = PLAY_BASE_PLANS.find(
    (entry) => entry.basePlanId === yearBasePlanId,
  )!;
  const monthly = PLAY_BASE_PLANS.find(
    (entry) => entry.basePlanId === "monthly",
  )!;
  return {
    productId: `plan_${plan.slug}` as const,
    planSlug: plan.slug,
    /** Active Console base plans only (Core year = annual, not yearly). */
    basePlans: [monthly, yearBase] as const,
  };
});

export type PlayCreditProductId = (typeof PLAY_CREDIT_PRODUCTS)[number]["productId"];
export type PlayAcademyProductId = (typeof PLAY_ACADEMY_PRODUCTS)[number]["productId"];
export type PlayPlanSlug = (typeof STUDIO_PLAN_CATALOG)[number]["slug"];
export type PlayBasePlanId = (typeof PLAY_BASE_PLANS)[number]["basePlanId"];

export type PlayGrant =
  | { kind: "credits"; productId: PlayCreditProductId; credits: number }
  | {
      kind: "academy";
      productId: PlayAcademyProductId;
      courseSlug: string;
      /** Web face this SKU covers, in TT cents. Not the Play list price. */
      webListCents: number;
      /** Set when the SKU is one seat. In-person Video Ad Mastery has no SKU. */
      seat?: "online";
    }
  | {
      kind: "subscription";
      productId: string;
      planSlug: PlayPlanSlug;
      interval: StudioPlanInterval;
      basePlanId: PlayBasePlanId;
    };

function assertCreditSkusMatchTiers() {
  if (PLAY_CREDIT_PRODUCTS.length !== TOP_UP_TIER_CREDITS.length) {
    throw new Error("Play credit SKUs drifted from TOP_UP_TIER_CREDITS");
  }
  PLAY_CREDIT_PRODUCTS.forEach((product, index) => {
    if (product.credits !== TOP_UP_TIER_CREDITS[index]) {
      throw new Error(
        `Play credit SKU ${product.productId} drifted from TOP_UP_TIER_CREDITS`,
      );
    }
  });
}

assertCreditSkusMatchTiers();

export function isLivePlayDeployment(siteUrl: string | undefined | null): boolean {
  if (!siteUrl) return false;
  try {
    const host = new URL(siteUrl).host;
    return (LIVE_PLAY_DEPLOYMENT_HOSTS as readonly string[]).includes(host);
  } catch {
    return false;
  }
}

export function playCreditConsoleList(credits: number): {
  webCents: number;
  webTtd: number;
  playListTtd: number;
  playListUsd: number;
} {
  const webCents = Math.round(credits * DEFAULT_CREDIT_PRICE_CENTS);
  const playListTtd = playListTtdFromWebCents(webCents);
  return {
    webCents,
    webTtd: webCents / 100,
    playListTtd,
    playListUsd: playListUsdFromPlayListTtd(playListTtd),
  };
}

export function playAcademyConsoleList(webListCents: number): {
  webCents: number;
  webTtd: number;
  playListTtd: number;
  playListUsd: number;
} {
  const webCents = Math.round(webListCents);
  const playListTtd = playListTtdFromWebCents(webCents);
  return {
    webCents,
    webTtd: webCents / 100,
    playListTtd,
    playListUsd: playListUsdFromPlayListTtd(playListTtd),
  };
}

export function playSubscriptionConsoleList(
  plan: Pick<
    StudioPlanCatalogEntry,
    "faceMonthlyCents" | "monthlyDiscountPercent" | "annualDiscountPercent"
  >,
  interval: StudioPlanInterval,
): {
  webCents: number;
  webTtd: number;
  playListTtd: number;
  playListUsd: number;
  faceMonthlyCents: number;
  monthlyCredits: number;
} {
  const quote = quoteStudioPlan(plan, interval, DEFAULT_CREDIT_PRICE_CENTS);
  const playListTtd = playListTtdFromWebCents(quote.chargeCents);
  return {
    webCents: quote.chargeCents,
    webTtd: quote.chargeCents / 100,
    playListTtd,
    playListUsd: playListUsdFromPlayListTtd(playListTtd),
    faceMonthlyCents: quote.faceMonthlyCents,
    monthlyCredits: quote.monthlyCredits,
  };
}

export function playCreditProduct(credits: number) {
  return (
    PLAY_CREDIT_PRODUCTS.find((product) => product.credits === credits) ?? null
  );
}

export function playAcademyProduct(courseSlug: string) {
  return (
    PLAY_ACADEMY_PRODUCTS.find((product) => product.courseSlug === courseSlug) ??
    null
  );
}

export function playSubscriptionProduct(
  planSlug: string,
  interval: StudioPlanInterval,
) {
  const product = PLAY_SUBSCRIPTION_PRODUCTS.find(
    (entry) => entry.planSlug === planSlug,
  );
  if (!product) return null;
  const basePlan =
    product.basePlans.find((entry) => entry.interval === interval) ?? null;
  if (!basePlan) return null;
  return {
    productId: product.productId,
    planSlug: product.planSlug,
    basePlanId: basePlan.basePlanId,
    interval: basePlan.interval,
  };
}

export function playGrantForProduct(
  productId: string,
  basePlanId?: string | null,
): PlayGrant | null {
  const credits = PLAY_CREDIT_PRODUCTS.find(
    (product) => product.productId === productId,
  );
  if (credits) {
    return {
      kind: "credits",
      productId: credits.productId,
      credits: credits.credits,
    };
  }
  const course = PLAY_ACADEMY_PRODUCTS.find(
    (product) => product.productId === productId,
  );
  if (course) {
    return {
      kind: "academy",
      productId: course.productId,
      courseSlug: course.courseSlug,
      webListCents: course.webListCents,
      ...("seat" in course ? { seat: course.seat } : {}),
    };
  }
  const plan = PLAY_SUBSCRIPTION_PRODUCTS.find(
    (product) => product.productId === productId,
  );
  if (!plan) return null;
  // Accept both Active purchase IDs and restore-safety aliases:
  // Core year is `annual` in Console; mistaken `yearly` on any plan still
  // resolves as a year interval. `annual` on Plus/Pro likewise maps to year.
  const basePlan = PLAY_BASE_PLANS.find((entry) => entry.basePlanId === basePlanId);
  if (!basePlan) return null;
  return {
    kind: "subscription",
    productId: plan.productId,
    planSlug: plan.planSlug,
    interval: basePlan.interval,
    basePlanId: basePlan.basePlanId,
  };
}

/**
 * Consumables are one token per purchase. Subscription renewals keep the token
 * and change the Play order id (GPA…, GPA….0, GPA….1).
 */
export function playExternalPaymentId(args: {
  purchaseToken: string;
  orderId?: string | null;
  kind: PlayGrant["kind"];
}): string {
  const token = args.purchaseToken.trim();
  const orderId = String(args.orderId ?? "").trim();
  if (args.kind === "subscription" && orderId) {
    return `play:${token}:${orderId}`;
  }
  return `play:${token}`;
}
